<template>
    <div class="space-y-4">
        <div class="p-2 space-y-6 bg-white lg:px-4">
            <!-- Primary Details  -->
            <div>
                <div class="mx-2 text-xl font-semibold">Primary Details</div>
                <div class="flex flex-col items-start md:flex-row">
                    <!-- logo image -->
                    <div class="m-2">
                        <div class="block text-sm font-semibold text-gray-700">Logo*</div>

                        <div
                            class="cursor-pointer"
                            @click="replaceImage('logo')"
                        >
                            <div
                                v-if="logoPreview"
                                class="w-52 h-52 rounded border border-gray-200 p-4 overflow-hidden"
                            >
                                <img
                                    :src="logoPreview"
                                    :alt="$page.props.user.name"
                                    class="w-full h-full object-cover"
                                />
                            </div>

                            <div
                                v-else
                                class="relative group w-52 h-52"
                            >
                                <div
                                    class="relative w-full h-full font-medium text-green-600 bg-white rounded-md cursor-pointer hover:text-green-500"
                                >
                                    <div
                                        class="flex justify-center w-full h-full px-6 pt-5 pb-6 mt-2 rounded-md"
                                        :class="
                                            localErrors.logo
                                                ? 'border-2 border-red-300 border-dashed'
                                                : 'border-2 border-gray-300 border-dashed'
                                        "
                                    >
                                        <div class="flex flex-col items-center justify-center space-y-1 text-center">
                                            <FileUploadIcon />
                                            <div class="flex items-center justify-center text-sm text-gray-600">
                                                <span>Upload Logo</span>
                                            </div>
                                            <p class="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <input
                                ref="logo-image-upload"
                                class="hidden"
                                type="file"
                                @change="filesChange($event.target.files, 'logo')"
                            />
                        </div>

                        <div
                            v-if="localErrors.logo"
                            class="mt-4 text-red-500"
                        >
                            {{ localErrors.logo }}
                        </div>
                    </div>

                    <div class="grid w-full grid-cols-1 gap-5 m-2 lg:grid-cols-2">
                        <!-- person name -->
                        <div>
                            <div class="block text-sm font-semibold text-gray-700">Person Name*</div>
                            <div class="flex mt-1 rounded-md shadow-sm">
                                <t-input
                                    v-model="form.personName"
                                    type="text"
                                    placeholder="Person Name"
                                    :variant="localErrors.person_name ? 'danger' : ''"
                                />
                            </div>
                            <div
                                v-if="localErrors.person_name"
                                class="text-red-500"
                            >
                                {{ localErrors.person_name }}
                            </div>
                        </div>

                        <!-- Email -->
                        <div>
                            <div class="block text-sm font-semibold text-gray-700">Email*</div>
                            <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                <t-input
                                    v-model="form.email"
                                    type="email"
                                    placeholder="Email"
                                    :variant="localErrors.email ? 'danger' : ''"
                                />
                                <div
                                    v-if="localErrors.email"
                                    class="text-red-500"
                                >
                                    {{ localErrors.email }}
                                </div>
                            </div>
                        </div>

                        <!-- Phone -->
                        <div>
                            <div class="block text-sm font-semibold text-gray-700">Phone*</div>
                            <div class="flex flex-col mt-1 rounded-md shadow-sm">
                                <t-input
                                    v-model="form.phone"
                                    type="number"
                                    placeholder="Phone"
                                    :variant="localErrors.phone ? 'danger' : ''"
                                />
                                <div
                                    v-if="localErrors.phone"
                                    class="text-red-500"
                                >
                                    {{ localErrors.phone }}
                                </div>
                            </div>
                        </div>

                        <!-- dob -->
                        <div>
                            <div class="block text-sm font-semibold text-gray-700">DOB*</div>
                            <div class="mt-1 rounded-md shadow-sm">
                                <DatePicker
                                    v-model="form.dob"
                                    class="form-input"
                                    format="YYYY-MM-DD"
                                    :disableFuture="true"
                                    :options="{ firstDay: 1 }"
                                />
                            </div>
                            <div
                                v-if="localErrors.dob"
                                class="text-red-500"
                            >
                                {{ localErrors.dob }}
                            </div>
                        </div>

                        <!-- gender -->
                        <div>
                            <div class="block text-sm font-semibold text-gray-700">Gender*</div>
                            <div class="-mt-1 rounded-md shadow-sm">
                                <select
                                    v-model="form.gender"
                                    :class="[
                                        'block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50',
                                        localErrors.gender ? 'border-red-500' : '',
                                    ]"
                                >
                                    <option
                                        disabled
                                        value=""
                                    />
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div
                                v-if="localErrors.gender"
                                class="text-red-500"
                            >
                                {{ localErrors.gender }}
                            </div>
                        </div>

                        <!-- role -->
                        <div>
                            <div class="block text-sm font-semibold text-gray-700">Role*</div>
                            <div class="-mt-1 rounded-md shadow-sm">
                                <select
                                    v-model="form.role"
                                    :class="[
                                        'block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50',
                                        localErrors.role ? 'border-red-500' : '',
                                    ]"
                                >
                                    <option
                                        disabled
                                        value=""
                                    />
                                    <option
                                        v-if="isSuperAdmin || isAdmin || isRetailer || (isWholesaler && kycVerified)"
                                        value="retailer"
                                    >
                                        Retailer
                                    </option>
                                    <option
                                        v-if="isSuperAdmin || (isWholesaler && !kycVerified)"
                                        value="wholesaler"
                                    >
                                        Wholesaler
                                    </option>
                                </select>
                            </div>

                            <div
                                v-if="localErrors.role"
                                class="text-red-500"
                            >
                                {{ localErrors.role }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import FileUpload from '@/Components/FileUpload.vue';
    import DatePicker from '@/Components/DatePicker.vue';
    import FileUploadIcon from '@/Components/Icons/FileUploadIcon.vue';

    export default {
        name: 'KYCStepOne',
        components: {
            FileUpload,
            FileUploadIcon,
            DatePicker,
        },
        props: {
            form: { type: Object, required: true },
            errors: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                logoPreview: this.form.logoPreview || null,
            };
        },
        computed: {
            localErrors() {
                return this.errors || {};
            },
        },
        methods: {
            // onFileChange(e) {
            //     const file = e.target.files[0];
            //     if (file) {
            //         const reader = new FileReader();
            //         reader.onload = (e) => {
            //             this.logoPreview = e.target.result;
            //             this.$emit('updateForm', { logo: this.logoPreview });
            //         };
            //         reader.readAsDataURL(file);
            //     }
            // },

            filesChange(fileList, type) {
                if (!fileList.length) return;
                if (type === 'logo') {
                    this.form.logo = fileList[0];
                    this.getImagePreviews('logo');
                }
            },

            getImagePreviews(attributeName) {
                if (/\.(jpe?g|png|gif)$/i.test(this.form[attributeName].name)) {
                    const reader = new FileReader();
                    reader.addEventListener(
                        'load',
                        () => {
                            const dataAttributeName = `${attributeName}Preview`;
                            this[dataAttributeName] = reader.result;
                        },
                        false,
                    );

                    reader.readAsDataURL(this.form[attributeName]);
                }
            },

            replaceImage(type) {
                this.$refs[`${type}-image-upload`].click();
            },
        },
    };
</script>
